import { apiClient } from './config.js';

export interface DashboardCounts {
  total_announcements: number;
  completed_announcements: number;
  failed_announcements: number;
  active_processing: number;
  total_articles: number;
  published_articles: number;
  draft_articles: number;
  approved_articles: number;
  uploaded_today: number;
  announcements_today: number;
  articles_today: number;
  backdated_announcements_today: number;
  backdated_articles_today: number;
  signals_published_total: number;
  signals_published_today: number;
}

export interface DashboardJob {
  job_id: string;
  announcement_id: string | null;
  job_type: string;
  status: string;
  priority: number;
  payload: Record<string, any>;
  result: Record<string, any> | null;
  error_message: string | null;
  attempts: number;
  max_attempts: number;
  created_at: string;
  updated_at: string;
  /** Next scheduled run for a pending/retrying job (delay or backoff);
   *  null for announcement 'process' rows. */
  next_run_at: string | null;
  /** True when pending AND next_run_at is in the future — the job is
   *  DELAYED (waiting on a timer), not ready backlog. */
  waiting: boolean;
  company_code: string | null;
  company_name: string | null;
  announcement_title: string | null;
  article_title: string | null;
}

export interface DashboardAttentionItem {
  /** `build_failed` is sourced from `publication_build_runs`, not the job
   *  queue: a build_deploy job carries neither an article_id nor a
   *  `destination` key, so the queue's supersession anti-join compared
   *  NULL to NULL and a failed build never cleared. The runs table also
   *  holds the real 11ty error rather than the dispatch envelope. */
  /** `promote_stale` = edits that reached preview but never reached
   *  production. An edit republishes to test, and the completed publish job
   *  triggers auto-promote so main gets a copy of test; when that chain breaks
   *  (the 2026-07-22 CAE class) main silently keeps the older render. Nothing
   *  detected it before — `behind_record`'s watermark is per-destination, not
   *  per-article. */
  /** `coverage_guarantee_breach` = a COVERAGE CUSTOMER's publishable filing
   *  did not reach a live article on every masthead, and no recorded planner
   *  decision explains it. P1 is non-negotiable, so this is a paid-obligation
   *  breach rather than a backlog item — and it is the signal that was missing
   *  when the 2026-08-03 rule deactivation went unnoticed for three weeks. */
  /** `build_memory_high` = a build peaked within 75% of its heap ceiling.
   *  A WARNING, not a failure — nothing is broken yet, which is the point.
   *  Added 2026-08-31 after Bell Wire hit two ceilings four hours apart with
   *  no measurement of either (scalability plan §7.5). */
  /** `mandated_coverage_breach` = a filing in a mandated event class (M&A,
   *  capital raise, capital restructure at substance ≥ 3) has no article on
   *  a routed masthead after the 60-minute SLA and nothing is in flight.
   *  One item per (filing, masthead), opened and closed by the backend
   *  sweep; the same episode drives the header alert chip (2026-09-17). */
  /** `duplicate_generation` = a filing got a SECOND article on one masthead
   *  (pipeline-health M2, 2026-10-05). One item per (masthead, UTC day), opened
   *  by the backend's pipeline lane and closed when the day re-reads clean or
   *  ages out; `target_name` is the masthead, `title` names the filings. */
  attention_type: 'failed_job' | 'build_failed' | 'promote_stale' | 'awaiting_approval' | 'coverage_stalled' | 'coverage_guarantee_breach' | 'build_memory_high' | 'mandated_coverage_breach' | 'duplicate_generation';
  item_id: string;
  label: string;
  detail: string | null;
  title: string | null;
  at: string;
  /** Article this attention item is about, when applicable. For failed
   *  publish/promote jobs this is the job's payload article_id (NULL on
   *  ingest/process failures that pre-date article creation). For
   *  awaiting_approval rows this mirrors item_id. The UI uses this to
   *  deep-link the card to the specific article. */
  article_id?: string | null;
  /** Announcement this attention item is about. Always populated for
   *  failed jobs (the job's announcement_id), present for
   *  awaiting_approval. Used as a fallback target when article_id is
   *  null. */
  announcement_id?: string | null;
  /** Masthead this item belongs to, when it has one. Populated for
   *  `build_failed` (and for failed publish/promote jobs, from the payload)
   *  so the card can route to that masthead rather than a generic list. */
  target_name?: string | null;
}

export interface QueueDepth {
  total: number;
  by_job_type: Record<string, number>;
  /** Pending jobs whose next_run_at is in the future — delayed, not ready. */
  waiting: number;
  /** total − waiting: the actionable backlog expected to move now. */
  ready: number;
  waiting_by_job_type: Array<{ job_type: string; count: number; next_due: string | null; last_due?: string | null }>;
}

export interface ArticlesBySiteRow {
  target_name: string;
  daily_target: number;
  generated_today: number;
  live_today: number;
  overall: number;
}

export interface ArticlesBySectorRow {
  target_name: string;
  resource_14d: number;
  nonresource_14d: number;
  unsectored_14d: number;
}

/** Coverage by sourcing lane — exchange (ASX/NSX/CXA/TSX/Other/Unclassified,
 *  announcement-sourced) or content path (Research/Synthesis). Lanes are
 *  mutually exclusive; article counts are the LIVE (non-archived) corpus, so
 *  Σ articles_total is the non-archived total, not the all-time header count. */
export interface CoverageLaneRow {
  key: string;
  kind: 'exchange' | 'path';
  announcements_total: number;
  announcements_today: number;
  articles_total: number;
  articles_published: number;
  articles_today: number;
}

/** LinkedIn URN resolver health (backend mig 225 event log + entities view).
 *  blocked = LinkedIn HTTP 999/429 refusals; clean_total = verified-URN
 *  taggable pool. */
export interface LinkedinResolverStats {
  queued: number;
  resolving: number;
  needs_review: number;
  clean_total: number;
  resolved_24h: number;
  blocked_24h: number;
  failed_24h: number;
}

/** Buffer API budget (mig 276): rate-limit windows passively captured from
 *  Buffer response headers by the publishing service, plus today's per-site
 *  push counts vs the daily cap. Replaces the URN card (2026-08-07). */
export interface BufferBudgetWindow {
  label: string;
  quota: number | null;
  remaining: number | null;
  reset_seconds: number | null;
  window_seconds: number | null;
}
export interface BufferBudgetChannel {
  renderer: string | null;
  platform: string;
  pushed_today: number;
  /** Accepted pushes in the rolling last 24h — what the cap and Buffer count. */
  pushed_24h?: number;
}
export interface BufferBudget {
  windows: BufferBudgetWindow[];
  captured_at: string | null;
  channels: BufferBudgetChannel[];
  daily_cap_enabled: boolean;
  daily_cap_per_site: number | null;
}

/** Firecrawl credit budget — live from Firecrawl's /team/credit-usage.
 *  plan_credits/billing period come from the API (a plan change appears
 *  automatically); null payload = fetch unavailable, card shows awaiting. */
/** LinkedIn Community Management API (Milestone A, 2026-09-20): today's
 *  per-endpoint usage against Dev-Tier quotas (the backend ledger is the
 *  only counter — LinkedIn sends no quota headers), token expiry and the
 *  taggable pool. `connected=false` → the card is not rendered. */
export interface LinkedInApiKeyUsage {
  endpoint_key: string;
  used_today: number;
  cap: number | null;
  pct: number | null;
  n_429: number;
}
export interface LinkedInApiBudget {
  connected: boolean;
  member_urn?: string | null;
  access_expires_in_days?: number;
  reconsent_due_in_days?: number | null;
  mastheads_total?: number;
  mastheads_administered?: number;
  keys?: LinkedInApiKeyUsage[];
  finder_queue_used_today?: number;
  resolve_budget?: number;
  taggable_total?: number;
  recheck_pending?: number;
  resolved_24h?: number;
}

export interface FirecrawlBudget {
  remaining_credits: number | null;
  plan_credits: number | null;
  consumed_credits: number | null;
  billing_period_start: string | null;
  billing_period_end: string | null;
  captured_at: string | null;
  /** Burn-rate projection to the reset (backend ≥ 0.3.63, 2026-10-06). */
  rate_per_day?: number;
  rate_basis?: string;
  days_left?: number;
  projected_at_reset?: number;
  over_by?: number;
  alert?: boolean;
  /** Pay-as-you-go forecast (backend ≥ 0.3.67): over plan is informational. */
  over_plan?: boolean;
  payg_increments?: number;
  payg_credits_per_increment?: number;
  payg_cost_usd?: number;
  payg_cost_aud_approx?: number;
  next_period_forecast?: number;
  next_period_over_by?: number;
}

export interface ReviewBlockedByTarget {
  target_name: string;
  count: number;
  primary_count: number;
  oldest_at: string | null;
}

/**
 * One halt we are waiting on (E2). Accountable-mode only — this is an
 * operator console, so the machinery is shown deliberately.
 */
export interface HaltWatchItem {
  event_id: string;
  company_code: string | null;
  purpose_class: string | null;
  /** The deadline in force — the company's own, or our structural fallback. */
  deadline: string | null;
  /**
   * TRUE when the deadline is the one the company stated in its filing;
   * FALSE when it is our ASX two-trading-day fallback. An operator reading
   * "due today" deserves to know which of those it is.
   */
  deadline_stated: boolean;
  halt_date: string | null;
  close_reason: string | null;
}

export interface HaltWatchQueue {
  outstanding: number;
  /** Deadline passed, sweep has not yet closed it. A real state, not a failure. */
  overdue: number;
  closed_last_7d: number;
  resolved_last_7d: number;
  lapsed_last_7d: number;
  items: HaltWatchItem[];
}

export interface ActionQueueBlock {
  review_blocked_by_target: ReviewBlockedByTarget[];
  /** E2 — halts we are expecting an announcement from, and by when. */
  halt_watch?: HaltWatchQueue;
  notifications_pending: number;
  notifications_oldest_at: string | null;
  announcements_failed_recent: number;
  announcements_failed_oldest_at: string | null;
  announcements_failed_aged: number;
  jobs_failed_recent: number;
  jobs_failed_oldest_at: string | null;
  jobs_failed_aged: number;
}

/** F1 (2026-09-22) — mandated event-class coverage by state, last 7 days.
 * Built on the breach sweep's own definitions, so `breached` equals the
 * mandated_coverage_breach attention chip. written + pending + breached ==
 * obligations, per masthead and in total. `superseded` is a chip, not a
 * state: the sweep counts withdrawn declarations on purpose. */
export interface MandatedCoverageBucket {
  obligations: number;
  written: number;
  pending: number;
  breached: number;
}
export interface MandatedCoverageItem {
  announcement_id: string;
  company_code: string | null;
  title: string | null;
  target_name: string;
  activity_sub_type: string | null;
  decided_at: string | null;
  age_minutes: number | null;
  state: 'pending' | 'breached';
  /** The sweep's vocabulary: generation_failed | plan_failed | planner_declined | no_plan | unknown; null = in flight. */
  reason: string | null;
  superseded: boolean;
}
export interface MandatedCoveragePanel extends MandatedCoverageBucket {
  days: number;
  sla_minutes: number;
  superseded: number;
  by_target: (MandatedCoverageBucket & { target_name: string })[];
  by_class: (MandatedCoverageBucket & { activity_sub_type: string })[];
  items: MandatedCoverageItem[];
}

export interface DependencyHealthRow {
  dependency: string;
  state: 'healthy' | 'degraded' | 'down';
  last_success_at: string | null;
  last_failure_at: string | null;
  consecutive_failures: number;
  last_failure_class: string | null;
  last_error_sample: string | null;
  state_changed_at: string;
  /** What the row can honestly claim (backend ≥ 0.3.44, 2026-10-06):
   *  external = another monitor owns it · unused = deliberately idle ·
   *  stale = no success in 7 days (or never) · ok */
  evidence?: 'ok' | 'stale' | 'external' | 'unused';
  evidence_note?: string | null;
}

export interface DependencyEpisode {
  episode_id: number;
  dependency: string;
  failure_class: string;
  signature_hash: string | null;
  opened_at: string;
  last_failure_at: string;
  closed_at: string | null;
  failure_count: number;
  sample_error: string | null;
  affected: string | null;
  backend_revision: string | null;
  publishing_revision: string | null;
}

export interface PlatformIncident {
  provider: string;
  /** dependency keys this provider maps to (same vocabulary as episodes) */
  dependencies: string[];
  name: string;
  status: string;
  impact: string;
  started_at: string | null;
  url: string;
  components: string[];
}

export interface PlatformStatusBlock {
  /** null until the backend's first status-page fetch completes */
  fetched_at: string | null;
  incidents: PlatformIncident[];
}

export interface SocialFlowBlock {
  /** distinct articles promoted in the last 24h (the demand signal) */
  promoted_24h: number;
  drafted_24h: number;
  pushed_24h: number;
  posted_24h: number;
  failed_24h: number;
  latest_fail_error: string | null;
  /** promoted articles that produced NO social draft — compose-stage gap */
  promoted_no_draft: number;
  /** approved >1h ago, never pushed — push worker stalled */
  approved_unpushed: number;
  /** pushed 3–24h ago, Buffer never posted (small numbers = slot queuing) */
  recent_stuck: number;
  /** pushed >24h, never reconciled (reconcile page-bound blind spot) */
  unreconciled_backlog: number;
  /** week spread: drafts waiting past their first day for a free Buffer slot */
  backlog_waiting?: number;
  /** week spread: backlog posts that hit the 3-day limit in the last 24h */
  aged_out_backlog_24h?: number;
}

export interface HeartbeatBlock {
  lanes: Record<'ingest' | 'generate' | 'publish' | 'promote' | 'social' | 'notify', string | null>;
  dependencies: DependencyHealthRow[];
  episodes: DependencyEpisode[];
  /** open episodes whose last failure is over 7 days old — collapsed to one line (2026-10-06) */
  episodes_open_aged?: number;
  deploy: { backend_revision: string | null };
  platform_status?: PlatformStatusBlock;
  social_flow?: SocialFlowBlock | null;
}

/** masthead_caps `_MODES` minus "off" (off returns no rows). */
export type MastheadCapMode = 'shadow' | 'enforce';

/** masthead_caps `exempt_reason()` return values. */
export type MastheadCapExemptReason = 'mandated' | 'price_sensitive' | 'coverage_customer';

export interface MastheadCapHeldItem {
  announcement_id: string;
  company_code: string | null;
  title: string | null;
  merit: number;
  rank: number;
  /** ISO UTC; the card computes the age at render. */
  held_since: string;
  generated: boolean;
}

/** One masthead's cap-queue snapshot for today (coverage-routing, CS509; Steve 2026-11-02). */
export interface MastheadCapSnapshot {
  /** Lowercase target name, as in `articles_by_site`. */
  target_name: string;
  /** YYYY-MM-DD, the masthead's cap day. */
  window_date: string;
  mode: MastheadCapMode;
  base: { published: number; allowance_now: number; target: number };
  leeway: { used: number; size: number };
  exempt: { total: number; by_reason: Partial<Record<MastheadCapExemptReason, number>> };
  queue: { limit: number; held: MastheadCapHeldItem[]; released: number; evicted: number; expired: number };
  skipped: number;
  decisions: number;
  /** Bell Wire only: the weekly budget. */
  week?: { budget: number; used: number; remaining: number; today_share: number; today_used: number };
}

export type SocialCapacityPlatform = 'linkedin' | 'twitter';

export interface SocialCapacityPlatformState {
  /** Null when the operator cap is off. */
  cap_24h: number | null;
  counted_24h: number;
  queued_in_buffer: number;
  oldest_queued_pushed_at: string | null;
  posted_today_sydney: number;
  drafts_waiting: { coverage: number; time_critical: number; fresh: number; backlog: number };
  /** Null when the operator cap is off. */
  slots_to_week_end: number | null;
  /** An estimate; null when the operator cap is off. */
  expected_drop_this_week: number | null;
  paused_until: string | null;
  last_pass_at: string | null;
}

/** Publishing `GET /social/capacity` body (socials), passed through by the backend. */
export interface SocialCapacity {
  as_of: string;
  week_start_sydney: string;
  window_hours: number;
  /** Keyed by lowercase target_name, as in `articles_by_site`. */
  sites: Record<string, {
    target_id: string;
    renderer: 'bellwire' | 'marketfloor' | 'orecast';
    platforms: Partial<Record<SocialCapacityPlatform, SocialCapacityPlatformState>>;
  }>;
}

/**
 * `GET /dashboard` capacity_queue (observability, 308dea7ad; Steve 2026-11-02). A failed half is
 * null with an `unavailable` entry, never zeros: render it as unavailable. `caps: []` means the caps
 * are off.
 */
export interface CapacityQueueBlock {
  caps: MastheadCapSnapshot[] | null;
  socials: SocialCapacity | null;
  unavailable: { part: 'caps' | 'socials'; reason: 'error' | 'timeout' | 'budget' }[];
}

export interface DashboardData {
  counts: DashboardCounts;
  linkedin_resolver?: LinkedinResolverStats;
  buffer_budget?: BufferBudget;
  firecrawl_budget?: FirecrawlBudget | null;
  linkedin_api?: LinkedInApiBudget;
  action_queue?: ActionQueueBlock;
  heartbeat?: HeartbeatBlock;
  active_jobs: DashboardJob[];
  recent_timeline: DashboardJob[];
  needs_attention: DashboardAttentionItem[];
  queue_depth?: QueueDepth;
  articles_by_site?: ArticlesBySiteRow[];
  articles_by_sector?: ArticlesBySectorRow[];
  coverage_by_lane?: CoverageLaneRow[];
  mandated_coverage?: MandatedCoveragePanel | null;
  /** Absent on a backend before 308dea7ad. */
  capacity_queue?: CapacityQueueBlock;
}

export async function archiveFailedJobs(jobIds: string[]): Promise<{ archived: number }> {
  const { data } = await apiClient.post('/tools/archive-failed-jobs', { job_ids: jobIds });
  return data;
}

export async function retryFailedJobs(jobIds: string[]): Promise<{ requeued: number }> {
  const { data } = await apiClient.post('/tools/retry-failed-jobs', { job_ids: jobIds });
  return data;
}

export async function archiveFailedAnnouncements(opts: { announcementIds?: string[]; olderThanHours?: number }): Promise<{ archived: number }> {
  const { data } = await apiClient.post('/tools/archive-failed-announcements', {
    announcement_ids: opts.announcementIds ?? [],
    older_than_hours: opts.olderThanHours ?? null,
  });
  return data;
}

/*
 * The dashboard fetch is SINGLE-FLIGHT and BACKS OFF (operator ruling 2026-10-06).
 *
 * 5 Oct 02:38Z: one browser polled /dashboard up to 52/min. Three components
 * poll it (Dashboard, Articles, the dependency chips in the layout), the API
 * client gave up at 30 s and react-query retried, so a slow response was
 * abandoned and re-fired while the server was still computing it. Now:
 *  - one request at a time: a caller while one is in flight shares it;
 *  - a 65 s timeout, past the gateway's 60 s, so we never abandon a request
 *    the server is still answering (the backend bounds itself to ~45 s);
 *  - after a failure, pollers wait (30 s doubling to 5 min) instead of
 *    re-polling on their normal interval, and never retry immediately.
 */
let dashboardInFlight: Promise<DashboardData> | null = null;

export function getDashboard(): Promise<DashboardData> {
  if (!dashboardInFlight) {
    dashboardInFlight = apiClient
      .get('/dashboard', { timeout: 65_000 })
      .then(({ data }) => data as DashboardData)
      .finally(() => { dashboardInFlight = null; });
  }
  return dashboardInFlight;
}

/** Poll interval for a dashboard query: `baseMs` while healthy; after a
 *  failure, 30 s doubling per consecutive failure, capped at 5 min. */
export function dashboardPollInterval(baseMs: number) {
  return (query: { state: { fetchFailureCount: number } }): number => {
    const fails = query.state.fetchFailureCount;
    if (fails <= 0) return baseMs;
    return Math.min(300_000, 30_000 * 2 ** (fails - 1));
  };
}

/** Shared options for every useQuery on ['dashboard']. */
export function dashboardQueryOptions(baseMs: number) {
  return {
    queryKey: ['dashboard'] as const,
    queryFn: getDashboard,
    refetchInterval: dashboardPollInterval(baseMs),
    refetchIntervalInBackground: false,
    retry: false,
  };
}
